import { useEffect, useState } from "react";
import type { Id, Scene } from "@supermanus/document-model";
import {
  formatArchiveOccurrenceLabel,
  MAX_ARCHIVED_SCENE_VERSIONS,
  VAULT_RETENTION_DAYS,
} from "@supermanus/document-model";
import type { ArchiveSceneMode } from "@supermanus/document-model";

function formatArchivedAt(iso?: string): string {
  if (!iso) return "Ukendt tidspunkt";
  try {
    return new Date(iso).toLocaleString("da-DK", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function ArchiveSceneDialog({
  sceneLabel,
  existing,
  onCancel,
  onConfirm,
}: {
  sceneLabel: string;
  existing: Scene[];
  onCancel: () => void;
  onConfirm: (options: {
    mode: ArchiveSceneMode;
    replaceArchivedSceneId?: Id;
    label?: string;
  }) => void;
}) {
  const atMax = existing.length >= MAX_ARCHIVED_SCENE_VERSIONS;
  const [mode, setMode] = useState<ArchiveSceneMode>(
    atMax ? "replaceOne" : existing.length === 0 ? "new" : "new",
  );
  const [replaceId, setReplaceId] = useState(existing[0]?.id ?? "");
  const [label, setLabel] = useState("");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const canSubmit =
    mode === "new"
      ? !atMax
      : mode === "replaceAll"
        ? existing.length > 0
        : Boolean(replaceId);

  return (
    <div className="sm-dialog-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="sm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-archive-scene-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sm-archive-scene-title">Arkivér scene</h2>
        <p>
          Der oprettes en kopi af {sceneLabel} i arkivet. Den aktuelle scene i
          manus forbliver redigerbar. Arkiverede versioner slettes automatisk
          efter {VAULT_RETENTION_DAYS} dage.
        </p>

        <label className="sm-dialog-field sm-archive-label-field">
          <span className="sm-archive-label-heading">
            Navn på denne forekomst
            <span className="sm-dialog-hint-inline"> (valgfrit)</span>
          </span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="fx Før interview"
            autoFocus
            maxLength={120}
          />
          <span className="sm-dialog-hint">
            Gør det nemt at kende versionerne fra hinanden. Scenens rigtige
            titel bevares og bruges, hvis du gendanner.
          </span>
        </label>

        {existing.length === 0 ? (
          <p className="sm-dialog-hint">
            Der er endnu ingen arkiverede versioner af denne scene.
          </p>
        ) : (
          <>
            <p className="sm-dialog-hint">
              Arkiverede versioner: {existing.length} af{" "}
              {MAX_ARCHIVED_SCENE_VERSIONS}.
              {atMax
                ? " Maksimum er nået — vælg at overskrive én eller alle."
                : null}
            </p>
            <fieldset className="sm-dialog-fieldset">
              <legend>Hvordan vil du arkivere?</legend>
              {!atMax ? (
                <label className="sm-setting">
                  <input
                    type="radio"
                    name="archive-mode"
                    checked={mode === "new"}
                    onChange={() => setMode("new")}
                  />
                  <span>Gem som ny version</span>
                </label>
              ) : null}
              <label className="sm-setting">
                <input
                  type="radio"
                  name="archive-mode"
                  checked={mode === "replaceOne"}
                  onChange={() => setMode("replaceOne")}
                />
                <span>Overskriv én eksisterende version</span>
              </label>
              {mode === "replaceOne" ? (
                <label className="sm-dialog-field">
                  Version der overskrives
                  <select
                    value={replaceId}
                    onChange={(e) => setReplaceId(e.target.value)}
                  >
                    {existing.map((s, i) => (
                      <option key={s.id} value={s.id}>
                        {formatArchiveOccurrenceLabel(s, i + 1)}
                        {s.archiveLabel?.trim()
                          ? ` — ${formatArchivedAt(s.archivedAt)}`
                          : ""}
                        {s.title.trim() ? ` (${s.title.trim()})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="sm-setting">
                <input
                  type="radio"
                  name="archive-mode"
                  checked={mode === "replaceAll"}
                  onChange={() => setMode("replaceAll")}
                />
                <span>Slet alle eksisterende og gem én ny</span>
              </label>
            </fieldset>
          </>
        )}
        <div className="sm-dialog-actions">
          <button type="button" onClick={onCancel}>
            Annuller
          </button>
          <button
            type="button"
            className="sm-dialog-primary"
            disabled={!canSubmit}
            onClick={() =>
              onConfirm({
                mode: existing.length === 0 ? "new" : mode,
                replaceArchivedSceneId:
                  mode === "replaceOne" ? replaceId : undefined,
                label: label.trim() || undefined,
              })
            }
          >
            Arkivér
          </button>
        </div>
      </div>
    </div>
  );
}
